/**
 * Resolve territory fill opacity for raster-backed maps.
 *
 * Most backdrop maps deliberately let the artwork show through ownership
 * colours. Some artwork already contains a light territory base, though, so
 * that compositing visibly washes out the player palette. Those maps can opt
 * into opaque ownership fills without changing the backdrop behavior of any
 * other map or the region-overlay emphasis rules.
 */
export function resolveBackdropFillOpacity({
  hasBackgroundImage,
  opaqueTerritoryFills,
  showRegionBonuses,
  isSelected,
  isSecondary,
  regionIsEmphasised,
  hasRegion,
  emphasisOpacity,
  regionOpacity,
  restingOpacity,
}: {
  hasBackgroundImage: boolean
  opaqueTerritoryFills?: boolean
  showRegionBonuses: boolean
  isSelected: boolean
  isSecondary: boolean
  regionIsEmphasised: boolean
  hasRegion: boolean
  emphasisOpacity: number
  regionOpacity: number
  restingOpacity: number
}): number | undefined {
  if (!hasBackgroundImage) return undefined
  if (opaqueTerritoryFills && !showRegionBonuses) return 1
  if (isSelected || isSecondary || regionIsEmphasised) return emphasisOpacity
  if (showRegionBonuses && hasRegion) return regionOpacity
  return restingOpacity
}

/**
 * Below this chroma ((max - min) / 255 of the RGB channels) a backdrop reads
 * as a neutral paper, stone or slate tone. Every shipped backdrop is under
 * 0.3; Europe Classic's vivid Warfish sea (#00c1f0) is 0.94.
 */
const NEUTRAL_BACKDROP_MAX_CHROMA = 0.5

function hexChroma(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1]!, 16)
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  return (Math.max(...channels) - Math.min(...channels)) / 255
}

/**
 * Colour of the regions-view spotlight wash over everything that is NOT the
 * focused region (and of the faded region chips). Normally that is the map's
 * own backdrop, so the unlit board recedes into the page. A vivid backdrop
 * would instead tint the whole board its colour, so it falls back to the
 * theme's neutral map canvas.
 */
export function resolveSpotlightDimColor(backdrop: string, themeOcean: string): string {
  const chroma = hexChroma(backdrop)
  return chroma !== null && chroma > NEUTRAL_BACKDROP_MAX_CHROMA ? themeOcean : backdrop
}
